import React from 'react';
import styled from 'styled-components';
import { AlertCircle, Inbox } from 'lucide-react';
import { Skeleton, EmptyState, Button } from '@kardume/ui';

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2.5rem 1.5rem;
  text-align: center;
  color: ${(p) => p.theme.error};
`;

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

interface DataStateProps {
  loading: boolean;
  error: string | null;
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  onRetry?: () => void;
  children: React.ReactNode;
}

/** Standard async boundary: loading -> error -> empty -> content. */
const DataState: React.FC<DataStateProps> = ({
  loading,
  error,
  isEmpty,
  emptyTitle = 'Nada por aqui ainda',
  emptyDescription,
  onRetry,
  children,
}) => {
  if (loading) {
    return (
      <SkeletonStack>
        <Skeleton height="56px" />
        <Skeleton height="56px" />
        <Skeleton height="56px" />
      </SkeletonStack>
    );
  }

  if (error) {
    return (
      <ErrorWrap>
        <AlertCircle size={28} />
        <p>{error}</p>
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            Tentar novamente
          </Button>
        )}
      </ErrorWrap>
    );
  }

  if (isEmpty) {
    return <EmptyState icon={<Inbox size={30} />} title={emptyTitle} description={emptyDescription} />;
  }

  return <>{children}</>;
};

export default DataState;
